import { nestCall, NestApiType } from '@/composables/nestApi';

export interface DuplicateTemplateParams {
  name?: string | null;
  idShopOfTemplate?: number | null;
  originCall?: string | null;
  idCampaign?: number | null;
  originTemplateId?: number | null;
  rootTemplateId?: number | null;
  skipRedirects?: boolean;
  resetTeaser?: boolean;
}

export interface DuplicateTemplateResponse {
  success: boolean;
  message?: string;
  data?: {
    id_template: number;
  };
}

export interface DuplicateTemplateBatchItem extends DuplicateTemplateParams {
  id_template: number;
}

export interface DuplicateTemplateBatchResponse {
  success: boolean;
  message?: string;
  data?: Array<{ id_template_original: number; id_template_new: number }>;
}

export interface MigrateTemplatePreviewResponse {
  success: boolean;
  message?: string;
  data?: {
    originalHtml: string;
    migratedHtml: string;
    warnings: string[];
    templateType: string;
  };
}

export interface MigrateTemplateConfirmResponse {
  success: boolean;
  message?: string;
  data?: {
    id_template: number;
    warnings: string[];
  };
}

export interface TemplateFetchElement {
  id_template_elements: string;
  id_template: number;
  data: string;
  html: string;
  css: string;
  translations: string;
  type: string;
}

export interface TemplateFetchResult {
  id_template: number;
  label: string;
  type: string;
  template_config: string | null;
  id_campaign: number | null;
  created_from_campaign: string | null;
  date_modification: string | null;
  version: number;
  elements: TemplateFetchElement[];
}

export function useTemplatesApi() {
  const duplicateTemplate = async (
    idTemplate: number,
    params: DuplicateTemplateParams = {},
  ): Promise<DuplicateTemplateResponse> => {
    const body: Record<string, unknown> = {};

    if (params.name != null) {
      body.name = params.name;
    }

    if (params.idShopOfTemplate != null) {
      body.id_shop_of_template = params.idShopOfTemplate;
    }

    if (params.originCall != null) {
      body.origin_call = params.originCall;
    }

    if (params.idCampaign != null) {
      body.id_campaign = params.idCampaign;
    }

    if (params.originTemplateId != null) {
      body.origin_template_id = params.originTemplateId;
    }

    if (params.rootTemplateId != null) {
      body.root_template_id = params.rootTemplateId;
    }

    if (params.skipRedirects !== undefined) {
      body.skip_redirects = params.skipRedirects;
    }

    if (params.resetTeaser !== undefined) {
      body.reset_teaser = params.resetTeaser;
    }

    try {
      const response = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        `/templates/${idTemplate}/duplicate`,
        {},
        '',
        body,
        true,
      );

      if (response && response.success) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        message: response?.message || 'Duplication failed',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Duplication failed',
      };
    }
  };

  const renameTemplate = async (
    idTemplate: number,
    name: string,
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await nestCall(
        'patch',
        NestApiType.CLIENT_API,
        `/templates/${idTemplate}/rename`,
        {},
        '',
        { name },
        true,
      );

      if (response && response.success) {
        return { success: true };
      }

      return {
        success: false,
        message: response?.message || 'Rename failed',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Rename failed',
      };
    }
  };

  const duplicateTemplateBatch = async (
    items: DuplicateTemplateBatchItem[],
  ): Promise<DuplicateTemplateBatchResponse> => {
    const body = items.map((item) => {
      const entry: Record<string, unknown> = { id_template: item.id_template };
      if (item.name != null) entry.name = item.name;
      if (item.idShopOfTemplate != null) entry.id_shop_of_template = item.idShopOfTemplate;
      if (item.originCall != null) entry.origin_call = item.originCall;
      if (item.idCampaign != null) entry.id_campaign = item.idCampaign;
      if (item.originTemplateId != null) entry.origin_template_id = item.originTemplateId;
      if (item.rootTemplateId != null) entry.root_template_id = item.rootTemplateId;
      if (item.skipRedirects !== undefined) entry.skip_redirects = item.skipRedirects;
      if (item.resetTeaser !== undefined) entry.reset_teaser = item.resetTeaser;
      return entry;
    });

    try {
      const response = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        '/templates/duplicate-batch',
        {},
        '',
        body,
        true,
      );

      if (response && response.success) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        message: response?.message || 'Batch duplication failed',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Batch duplication failed',
      };
    }
  };

  const fetchTemplates = async (
    ids: number[],
  ): Promise<Record<number, TemplateFetchResult>> => {
    if (!ids || ids.length === 0) return {};

    try {
      const response = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        '/templates/fetch',
        {},
        '',
        { ids },
        true,
      );

      if (response && response.success && response.data) {
        const normalized: Record<number, TemplateFetchResult> = {};
        for (const key of Object.keys(response.data)) {
          normalized[Number(key)] = response.data[key];
        }
        return normalized;
      }

      return {};
    } catch (error: any) {
      return {};
    }
  };

  const updateTemplates = async (
    updates: any[],
  ): Promise<{ success: boolean; message?: string }> => {
    if (!updates || updates.length === 0) return { success: true };

    try {
      const response = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        '/templates/update',
        {},
        '',
        { updates },
        true,
      );

      if (response && response.success) return { success: true };
      return { success: false, message: response?.message };
    } catch (error: any) {
      return { success: false, message: error?.message };
    }
  };

  const migrateTemplatePreview = async (
    idTemplate: number,
  ): Promise<MigrateTemplatePreviewResponse> => {
    try {
      const response = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        `/templates/${idTemplate}/migrate/preview`,
        {},
        '',
        {},
        true,
      );

      if (response && response.success) {
        return { success: true, data: response.data };
      }

      return { success: false, message: response?.message || 'Preview failed' };
    } catch (error: any) {
      return { success: false, message: error?.message };
    }
  };

  const migrateTemplateConfirm = async (
    idTemplate: number,
    name?: string,
  ): Promise<MigrateTemplateConfirmResponse> => {
    try {
      const body: Record<string, unknown> = {};
      if (name) body.name = name;

      const response = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        `/templates/${idTemplate}/migrate/confirm`,
        {},
        '',
        body,
        true,
      );

      if (response && response.success) {
        return { success: true, data: response.data };
      }

      return { success: false, message: response?.message || 'Migration failed' };
    } catch (error: any) {
      return { success: false, message: error?.message };
    }
  };

  return {
    duplicateTemplate,
    duplicateTemplateBatch,
    fetchTemplates,
    updateTemplates,
    renameTemplate,
    migrateTemplatePreview,
    migrateTemplateConfirm,
  };
}
